"use client";

import LoadingText from "@/components/common/loading-text";
import { COMMUNITY_JOIN_API, CREATE_PAYMENT_CHECKOUT_SESSION_API } from "@/lib/apis";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import {
	IGetJoinCommunityStatusResponseDTO,
	IJoinCommunityQueryData,
	IJoinCommunityResponseDTO,
} from "@/types/community";
import { useMutation, UseMutationOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import toast from "react-hot-toast";
import JoinChannelButton from "./join-channel-button";
import LeaveCommunityAlertDialog from "./leave-community-alert-dialog";

interface JoinCommunityButtonProps {
	communityId: string;
	channelId?: string;
	displayName?: string;
	className?: string;
}

const fetchCommunityJoinStatus = async (communityId: string, channelId?: string) => {
	const url = new URL(COMMUNITY_JOIN_API);

	if (channelId) {
		url.searchParams.set("channel-id", channelId);
	} else if (communityId) {
		url.searchParams.set("community-id", communityId);
	}

	const response = await fetch(url);

	if (!response.ok) {
		throw new Error("Failed to fetch join status");
	}

	return response.json();
};

const JoinCommunityButton = ({ communityId, channelId, displayName="Join", className }: JoinCommunityButtonProps) => {
	const router = useRouter();
	const { isLoggedIn, isLoading: isAuthLoading } = useAuth();
	const { openAuthModal } = useModal();
	const queryClient = useQueryClient();

	const { data: joinStatus, isLoading: isLoadingJoinStatus } =
		useQuery<IGetJoinCommunityStatusResponseDTO>({
			queryKey: ["join-status", communityId, channelId],
			queryFn: () => fetchCommunityJoinStatus(communityId, channelId),
			refetchOnWindowFocus: false,
		});

	const channelPaymentSettings = joinStatus?.data.settings;

	const { mutate: toggleMembership, isLoading } = useMutation<
		IJoinCommunityResponseDTO,
		Error,
		void,
		{ previousData: IJoinCommunityQueryData | undefined }
	>(
		async () => {
			const body = channelId ? { channelId } : { communityId };

			const response = await fetch(COMMUNITY_JOIN_API, {
				method: joinStatus?.data.joined ? "DELETE" : "POST",
				body: JSON.stringify(body),
			});

			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message);
			}

			return data;
		},
		{
			onMutate: () => {
				if (!channelPaymentSettings?.isPaid) {
					queryClient.setQueryData<IJoinCommunityQueryData>(
						["join-status", communityId, channelId],
						{
							data: {
								joined: channelId ? !joinStatus?.data.joined : false,
								requested: joinStatus?.data.requested || false,
							},
						}
					);
				}
			},
			onSuccess: async (data) => {
				// If the channel is paid, redirect to the checkout page
				const redirectToCheckout = data.data?.redirectToCheckout;

				if (redirectToCheckout) {
					const createCheckoutSession = await fetch(CREATE_PAYMENT_CHECKOUT_SESSION_API, {
						method: "POST",
						body: JSON.stringify({
							channelId,
						}),
					});

					if (!createCheckoutSession.ok) {
						toast.error("Failed to create checkout session");

						return;
					}

					const checkoutSession = await createCheckoutSession.json();

					const url = checkoutSession.data?.url;

					if (url) {
						router.push(url);
					}

					return;
				}

				toast.success(data.message);

				queryClient.invalidateQueries(["join-status", communityId, channelId]);
			},
			onError: (error, __, context) => {
				toast.error(error.message);

				if (context?.previousData) {
					queryClient.setQueryData<IJoinCommunityQueryData>(
						["join-status", communityId, channelId],
						context.previousData
					);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: ["join-status", communityId, channelId] });
			},
		} as UseMutationOptions<
			IJoinCommunityResponseDTO,
			Error,
			void,
			{ previousData: IJoinCommunityQueryData | undefined }
		>
	);

	if (!isLoggedIn) {
		return (
			<button
				type="button"
				onClick={() => openAuthModal(() => toggleMembership())}
				className="flex w-full items-center justify-center gap-1 rounded-md bg-brand-primary px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 md:w-44"
			>
				<PlusIcon className="h-5 w-5" />
				<span>Join</span>
			</button>
		);
	}

	if (joinStatus?.data.requested) {
		return (
			<button
				disabled
				type="button"
				className="flex w-full items-center justify-center gap-1 rounded-md bg-gray-400 px-4 py-2 font-medium text-white md:w-44"
			>
				<span>Requested</span>
			</button>
		);
	}

	if (channelId) {
		return joinStatus?.data.joined ? (
			<LeaveCommunityAlertDialog communityId={communityId} channelId={channelId} className={className} />
		) : (
			<JoinChannelButton
				onClick={() => toggleMembership()}
				paymentSettings={channelPaymentSettings}
				isLoading={isLoading || isLoadingJoinStatus}
				displayName={displayName}
				className={className}
			/>
		);
	}

	// Hide the button if the type is community and user is already a member
	return joinStatus?.data.joined ? (
		<LeaveCommunityAlertDialog communityId={communityId} />
	) : (
		// TODO: Extract this to a component
		<button
			type="button"
			disabled={isLoading}
			onClick={() => toggleMembership()}
			className="flex w-full items-center justify-center gap-1 rounded-md bg-brand-primary px-4 py-2 font-medium text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 md:w-44"
		>
			{isAuthLoading || isLoadingJoinStatus ? (
				<LoadingText classNames={{ loader: "stroke-white" }} />
			) : (
				<>
					<span>Join</span>
					<PlusIcon className="h-5 w-5" />
				</>
			)}
		</button>
	);
};

export default JoinCommunityButton;
